import { folderPath, folderEntries } from '../../../../engine/core/vault-folders.js';
import { t } from '../../../../shared/i18n/index.js';

// ensureOwnerDocAction's notice, one full sentence per action.
const CANNOT_KEYS = {
  rename: "mobile.docs.cannotRename",
  delete: "mobile.docs.cannotDelete",
  move: "mobile.docs.cannotMove",
};
export function createDocActions(deps) {
  const {
    getCallbacks,
    uiState,
    ensurePendingEditsSavedBeforeNavigation,
    scheduleRender,
    showMobileError,
  } = deps;

  const getIndex = () => getCallbacks()?.VaultStorage?.getIndex?.();

  function findDoc(docId) {
    const index = getIndex();
    return (index?.docs || []).find((doc) => doc.docId === docId) || null;
  }

  function buildFolderTrail(folders, activeFolderId) {
    const path = folderPath(folders || [], activeFolderId);
    return path.length ? path : [{ id: null, name: t("html.vault.title") }];
  }

  function findFolder(folderId) {
    const index = getIndex();
    return (index?.folders || []).find((folder) => folder.id === folderId) || null;
  }

  function getCurrentDocTitle(appState) {
    const callbacks = getCallbacks();
    const docId = appState.currentDocId || callbacks?.VaultStorage?.getCurrentDocId?.();
    if (!docId) return "";
    const doc = findDoc(docId);
    return doc?.title || doc?.name || "";
  }

  function getRootFolderId(index) {
    const roots = (index?.folders || []).filter(folder => folder.parentId === null);
    return (roots.find(folder => folder._storage === 'online') || roots[0])?.id || null;
  }

  function getActiveDocsFolderId(index) {
    return uiState.docsFolderId || getRootFolderId(index) || null;
  }

  function normalizeFolderId(folderId, rootFolderId) {
    return folderId || rootFolderId || null;
  }

  function isOwnerDoc(doc) {
    return !doc.role || doc.role === "owner";
  }

  function ensureOwnerDocAction(docId, action) {
    const callbacks = getCallbacks();
    const doc = findDoc(docId);
    if (doc && isOwnerDoc(doc)) return true;
    callbacks?.showToast?.(
      t(CANNOT_KEYS[action] || "mobile.docs.cannotChange"),
      { type: "warning" },
    );
    return false;
  }

  function docMatchesQuery(doc, query) {
    const title = (doc.title || doc.name || "").toLowerCase();
    const role = (doc.role || "owner").toLowerCase();
    return title.includes(query) || role.includes(query);
  }

  function navigateUpFolder() {
    const index = getIndex();
    const activeFolderId = getActiveDocsFolderId(index);
    const current = (index?.folders || []).find((folder) => folder.id === activeFolderId);
    uiState.docsFolderId = current?.parentId || getRootFolderId(index);
    scheduleRender();
  }

  async function openDoc(docId) {
    const callbacks = getCallbacks();
    if (!docId) return;
    if (!ensurePendingEditsSavedBeforeNavigation()) return;
    try {
      await callbacks?.openDoc?.(docId);
      uiState.tab = "outline";
      uiState.activeNodeId = null;
      uiState.activeNoteId = null;
      scheduleRender();
    } catch (err) {
      showMobileError(t("mobile.error.openDoc"), err);
    }
  }

  async function createDocFromForm() {
    const callbacks = getCallbacks();
    const title = document.getElementById("mobile-create-doc-title")?.value.trim() || t("mobile.form.newDocument");
    if (!title) return;
    if (!ensurePendingEditsSavedBeforeNavigation()) return;
    try {
      const result = await callbacks?.createDoc?.(title, getActiveDocsFolderId(getIndex()));
      if (result?.docId) {
        uiState.tab = "outline";
      }
    } catch (err) {
      showMobileError(t("mobile.error.createDoc"), err);
      return;
    }
    // Always close the form sheet on completion (success or no-op result).
    // Errors return early above so the form stays open with the typed title.
    uiState.form = null;
    uiState.formDraft = null;
    scheduleRender();
  }

  async function createFolderFromForm() {
    const callbacks = getCallbacks();
    const name = document.getElementById("mobile-create-folder-name")?.value.trim() || t("mobile.form.newFolder");
    if (!name) return;
    const parentId = uiState.form?.parentId || getActiveDocsFolderId(getIndex());
    try {
      await callbacks?.createFolder?.(name, parentId);
      uiState.form = null;
      uiState.formDraft = null;
      scheduleRender();
    } catch (err) {
      showMobileError(t("mobile.error.createFolder"), err);
    }
  }

  async function renameDocFromForm() {
    const callbacks = getCallbacks();
    const docId = uiState.form?.docId;
    const title = document.getElementById("mobile-rename-doc-title")?.value.trim();
    if (!docId || !title) return;
    if (!ensureOwnerDocAction(docId, "rename")) {
      uiState.form = null;
      uiState.formDraft = null;
      scheduleRender();
      return;
    }
    try {
      await callbacks?.renameDoc?.(docId, title);
      uiState.form = null;
      uiState.formDraft = null;
      scheduleRender();
    } catch (err) {
      showMobileError(t("mobile.error.renameDoc"), err);
    }
  }

  async function renameFolderFromForm() {
    const callbacks = getCallbacks();
    const folderId = uiState.form?.folderId;
    const name = document.getElementById("mobile-rename-folder-name")?.value.trim();
    if (!folderId || !name) return;
    try {
      await callbacks?.renameFolder?.(folderId, name);
      uiState.form = null;
      uiState.formDraft = null;
      scheduleRender();
    } catch (err) {
      showMobileError(t("mobile.error.renameFolder"), err);
    }
  }

  function requestDeleteDoc(docId) {
    const doc = findDoc(docId);
    if (!doc || !isOwnerDoc(doc)) return;
    uiState.docActionsId = null;
    uiState.confirm = {
      type: "delete-doc",
      docId,
      title: t("mobile.confirm.deleteDocTitle"),
      message: t("mobile.confirm.deleteDoc", { name: doc.title || doc.name || t("mobile.confirm.thisDocument") }),
    };
    scheduleRender();
  }

  function requestDeleteFolder(folderId) {
    const callbacks = getCallbacks();
    const folder = findFolder(folderId);
    const index = getIndex();
    if (!folder || !index) return;
    if (folder.parentId === null) {
      callbacks?.showToast?.(t("mobile.toast.rootFolder"), { type: "warning" });
      uiState.folderActionsId = null;
      scheduleRender();
      return;
    }
    const docsCount = (index.docs || []).filter((doc) => doc.folderId === folderId).length;
    const foldersCount = (index.folders || []).filter((item) => item.parentId === folderId).length;
    const name = folder.name || t("mobile.confirm.thisFolder");
    const message = docsCount || foldersCount
      ? t("mobile.confirm.deleteFolderMove", { name, parent: findFolder(folder.parentId)?.name || t("mobile.confirm.rootFolder") })
      : t("mobile.confirm.deleteFolder", { name });
    uiState.folderActionsId = null;
    uiState.confirm = {
      type: "delete-folder",
      folderId,
      title: t("mobile.confirm.deleteFolderTitle"),
      message,
    };
    scheduleRender();
  }

  async function deleteDoc(docId) {
    const callbacks = getCallbacks();
    if (!docId) return;
    if (!ensureOwnerDocAction(docId, "delete")) {
      if (uiState.docActionsId === docId) uiState.docActionsId = null;
      scheduleRender();
      return;
    }
    await callbacks?.deleteDoc?.(docId);
    if (uiState.docActionsId === docId) uiState.docActionsId = null;
    scheduleRender();
  }

  async function deleteFolder(folderId) {
    const callbacks = getCallbacks();
    if (!folderId) return;
    const folder = findFolder(folderId);
    if (!folder) {
      callbacks?.showToast?.(t("mobile.toast.folderUnavailable"), { type: "warning" });
      return;
    }
    if (folder.parentId === null) {
      callbacks?.showToast?.(t("mobile.toast.rootFolder"), { type: "warning" });
      return;
    }
    const index = getIndex();
    const rootFolderId = folder.parentId;
    if (!rootFolderId) {
      showMobileError(t("mobile.error.deleteFolder"), new Error("root folder unavailable"));
      return;
    }
    await callbacks?.deleteFolder?.(folderId, rootFolderId);
    if (uiState.docsFolderId === folderId) {
      uiState.docsFolderId = rootFolderId;
    }
    if (uiState.folderActionsId === folderId) uiState.folderActionsId = null;
    scheduleRender();
  }

  async function moveDocToFolder(folderId) {
    const callbacks = getCallbacks();
    const docId = uiState.moveDocId;
    if (!docId || !folderId) return;
    if (!ensureOwnerDocAction(docId, "move")) {
      uiState.moveDocId = null;
      scheduleRender();
      return;
    }
    try {
      await callbacks?.moveDoc?.(docId, folderId);
      uiState.moveDocId = null;
      scheduleRender();
    } catch (err) {
      showMobileError(t("mobile.error.moveDoc"), err);
    }
  }

  function collectFolderEntries(index) {
    return folderEntries(index?.folders || []);
  }

  return {
    buildFolderTrail,
    collectFolderEntries,
    createDocFromForm,
    createFolderFromForm,
    deleteDoc,
    deleteFolder,
    docMatchesQuery,
    ensureOwnerDocAction,
    findDoc,
    findFolder,
    getActiveDocsFolderId,
    getCurrentDocTitle,
    getRootFolderId,
    isOwnerDoc,
    moveDocToFolder,
    navigateUpFolder,
    normalizeFolderId,
    openDoc,
    renameDocFromForm,
    renameFolderFromForm,
    requestDeleteDoc,
    requestDeleteFolder,
  };
}
